import React, { useEffect, useState } from "react";
import { useAuth } from "../context/auth-context.js";
import {
  getUrlPath,
  checkForRedirect,
  getMLOData,
  searchFirebase,
  getUrl,
  getBrand,
  getCurrentTemplate,
  userBrandIsAllowed,
} from "../util/commonFunctions.js";
import DynamicSections from "../components/DynamicSections.js";
import GetInTouchModal from "../components/common/GetInTouchModal.js";
import SideBar from "../components/common/SideBar.js";
import NavBar from "../components/common/NavBar.js";
import BottomBar from "../components/common/BottomBar.js";
import ContactSection from "../components/ContactSection.js";
import HeroSection from "../components/HeroSection.js";
import Footer from "../components/common/Footer.js";
import ErrorPage from "./ErrorPage.js";
import LoadingPage from "./LoadingPage.js";
import ContactMe from "../components/dynamic-sections/ContactMe.js";
import AboutMe from "../components/dynamic-sections/AboutMe.js";
import { Helmet } from "react-helmet";

export default function HomePage() {
  const { currentUser, setCurrentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [errorPage, setErrorPage] = useState(false);
  let urlPath = getUrlPath();
  let url = getUrl();

  // GETTING THE MLO INFORMATION, BRAND, AND CUSTOMIZATIONS
  useEffect(() => {
    if (!urlPath) return;

    const loadData = async () => {
      try {
        // Check for redirects before loading MLO data
        await checkForRedirect(urlPath);

        const [mloData, details, branding] = await Promise.all([
          getMLOData(urlPath),
          searchFirebase(urlPath),
          getBrand(url),
        ]);

        console.log("values", mloData, details, branding);

        if (mloData?.message) {
          setErrorPage(true);
          setLoading(false);
          return;
        }

        if (mloData?.MLO_Page__c === "False") {
          setErrorPage(true);
          setLoading(false);
          return;
        }

        if (!branding) {
          setErrorPage(true);
          setLoading(false);
          return;
        }

        // A hyphenated URL resolves to the dotted unique name (justin-brubaker ->
        // justin.brubaker), so the customizations live under the name Salesforce
        // matched rather than the one in the URL.
        let customizations = details;
        const matchedId = mloData?.Account;

        if (matchedId && matchedId.toLowerCase() !== urlPath.toLowerCase()) {
          customizations = await searchFirebase(matchedId);
        }

        if (!userBrandIsAllowed(mloData, customizations, branding)) {
          setErrorPage(true);
          setLoading(false);
          return;
        }

        setCurrentUser({
          details: mloData,
          customizations: customizations,
          branding: branding,
        });

        setTimeout(() => {
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error("Error loading page data:", error);
        setErrorPage(true);
        setLoading(false);
      }
    };

    loadData();
  }, [urlPath]);

  if (errorPage) return <ErrorPage />;
  if (loading) return <LoadingPage />;
  if (!currentUser) return <ErrorPage />;

  console.log(currentUser);

  // replace www. with nothing

  let defaultBioContent = `${currentUser.details.FirstName} ${currentUser.details.LastName} offering home financing solutions for your homeownership journey. Contact me today to learn more.`;
  let bioContent = currentUser.details.About_Mue__c
    ? currentUser.details.About_Me__c.replace(/<[^>]*>/g, "")
        .replace(/\u200B/g, "")
        .slice(0, 160)
    : defaultBioContent;
  const template = getCurrentTemplate(currentUser);
  const isTemplateTwo = template === 2;

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/png" href={currentUser.branding.favicon} />
        <title>
          {currentUser.details.FirstName} {currentUser.details.LastName} | {currentUser.branding.name}
        </title>
        <meta name="description" content={bioContent}></meta>
      </Helmet>
      <div className={`h-full flex flex-col ${isTemplateTwo ? "" : "md:flex-row"}`}>
        {!isTemplateTwo && (
          <>
            <div className="hidden md:block h-screen z-50 w-1/5 max-w-[450px] min-w-[250px] fixed">
              <SideBar currentUser={currentUser} />
            </div>
            <div className="md:hidden block ">
              <SideBar currentUser={currentUser} />
            </div>
          </>
        )}
        <div className={`w-full ${isTemplateTwo ? "" : "md:w-4/5 ml-auto"}`}>
          {!isTemplateTwo ? <NavBar currentUser={currentUser} /> : null}
          {currentUser?.showGetInTouchModal && <GetInTouchModal currentUser={currentUser} />}
          {isTemplateTwo ? <HeroSection currentUser={currentUser} /> : null}
          {isTemplateTwo && currentUser?.details?.About_Me__c ? <AboutMe currentUser={currentUser} /> : null}
          {isTemplateTwo ? <BottomBar currentUser={currentUser} /> : null}
          {!currentUser?.customizations ? (
            <ContactMe currentUser={currentUser} />
          ) : !isTemplateTwo ? (
            <div className="md:h-14"></div>
          ) : null}
          <div className={isTemplateTwo ? "overflow-x-hidden" : ""}>
            <DynamicSections currentUser={currentUser} />
          </div>
          <div className="container mx-auto px-4">
            <ContactSection currentUser={currentUser} />
          </div>
          <Footer currentUser={currentUser} />
        </div>
      </div>
    </>
  );
}
